import { styled, x } from '@xstyled/styled-components';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Redirect } from 'react-router-dom';
import { t } from 'ttag';
import { LearnerTrainingStatus, usePostReviewMutation } from '~/data';
import { select } from '~/lib/select';
import { Flex } from '~/ui/flex/Flex';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { Logo } from '~/ui/logo/Logo';
import { Spinner } from '~/ui/spinner/Spinner';
import { TrainingState, useTrainingStore } from '../state';
import { useTrainingUrl } from '../useTrainingUrl';
import { AcceptPolicy } from './AcceptPolicy';
import { FeedbackForm } from './FeedbackForm';
import { postMessage } from '~/shell/PostMessage';
import { useTrainingParams } from '../useTrainingParams';

interface RxdInterface {
  SetScore(score: number, maxScore: number, minScore: number): void;
  SetReachedEnd(): void;
  SetPassed(): void;
}

interface WindowWithRxd extends Window {
  RXD?: RxdInterface;
}

const selector = (s: TrainingState) =>
  select(
    s,
    'status',
    'logoUrl',
    'scorePercent',
    'policyAcknowledgement',
    'featureShowReviewStep',
    'policiesForSignature',
    'certificateUrl'
  );

const ContainerForFeedback = styled.div`
  width: 550px;
  margin-left: auto;
  margin-right: auto;

  .LlamaModalOverlay {
    position: unset;
  }

  @media (max-width: sm) {
    width: 100%;

    .modalStyleForDarkTheme > svg:first-of-type {
      display: none;
    }
  }
`;

const StyledLlamaModal = styled(LlamaModal)`
  .instructions {
    font-size: 14px;
    line-height: 20px;
  }
  @media (max-width: sm) {
    padding-top: 0px !important;
    align-items: center !important;

    div:first-child {
      padding-top: 10px;

      .FullLlamaImg {
        display: none;
      }

      .PointerForModal {
        display: none;
      }
    }
  }
`;

export const Feedback = () => {
  const {
    status,
    logoUrl,
    scorePercent,
    policyAcknowledgement,
    featureShowReviewStep,
    policiesForSignature,
    certificateUrl
  } = useTrainingStore(selector);
  const [postReview, { called, loading, data, error }] =
    usePostReviewMutation();
  const url = useTrainingUrl();
  const { learnerTrainingId } = useTrainingParams();

  // This governs whether the policy acceptance component should be shown.
  // The default is true if there is a policy and the config says it needs to be acknowledged
  const [policyAccepted, setPolicyAccepted] = useState(
    !(
      policyAcknowledgement &&
      policiesForSignature &&
      policiesForSignature.length
    )
  );

  if (status !== LearnerTrainingStatus.Completed) {
    // We don't show this
    return <Redirect to={url!} />;
  }

  let RXD: RxdInterface | undefined;

  try {
    if (window.document.referrer) {
      const preUrl = window?.document?.referrer;
      const domain = preUrl?.split('/')[2];

      if (domain !== 'localhost:8000' && domain !== 'www.easyllama.com') {
        RXD = (window.parent as WindowWithRxd).RXD;
      }
    } else {
      RXD = (window.parent as WindowWithRxd).RXD;
    }
  } catch (error) {
    // console.error('RXD:', error);
  }

  useEffect(() => {
    if (policyAccepted) {
      postMessage(
        learnerTrainingId || '',
        certificateUrl || '',
        'completed',
        scorePercent
      );

      if (typeof RXD !== 'undefined') {
        RXD.SetScore(scorePercent, 100, 0);
        RXD.SetReachedEnd();
        RXD.SetPassed();
      }

      if (window && window.document) {
        const body = window.document.body;
        body.style.backgroundColor = 'var(--color-green-lt)';
      }
    }
  }, [policyAccepted]);

  if (loading) {
    return (
      <LlamaModal vertical="center" horizontal="center">
        <Spinner />
      </LlamaModal>
    );
  }

  if (error || data?.review?.errors.length) {
    return (
      <LlamaModal vertical="center" horizontal="center">
        <x.p p="lg">{t`We're sorry, an unexpected error has occurred`}</x.p>
      </LlamaModal>
    );
  }

  if (!policiesForSignature || policiesForSignature?.length === 0) {
    if (called || !featureShowReviewStep) {
      return <Redirect to={`${url}/success`} />;
    }
  }

  return createPortal(
    <ContainerForFeedback>
      <StyledLlamaModal>
        <Flex
          variant="list"
          p="lg"
          gap="10px"
          maxW="395px"
          textAlign="center"
          paddingLeft="20px"
          paddingRight="20px"
          margin="auto"
        >
          <x.div display="flex" justifyContent="center">
            <Logo src={logoUrl} height="36" />
          </x.div>
          <x.h2 fontFamily="llama" fontSize="36px">{t`Almost There!`}</x.h2>
          {featureShowReviewStep && policyAccepted ? (
            <FeedbackForm postReview={postReview} />
          ) : (
            <AcceptPolicy
              accept={() => {
                if (
                  !featureShowReviewStep &&
                  policiesForSignature &&
                  policiesForSignature?.length
                ) {
                  window.location.href = `${url}/success`;
                } else {
                  setPolicyAccepted(true);
                }
              }}
            />
          )}
        </Flex>
      </StyledLlamaModal>
    </ContainerForFeedback>,
    document.getElementById('root')!
  );
};
